import { isDefined, isNotDefined } from '@sgme/fp';
import { HStack, Spacer, VStack } from '@sgme/ui';
import { useIntl } from 'react-intl';

import { ExecutedTileField } from '@/App/tiles/orders/components/ExecutedTileField.tsx';
import { ProgressBar } from '@/App/tiles/orders/components/ProgressBar.tsx';
import { Summary } from '@/App/tiles/orders/components/Summary.tsx';
import { ExecutionPendingOrderStatus } from '@/App/tiles/orders/ExecutionPendingOrderStatus.tsx';
import { useFixingTileId } from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { ActionButtons } from '@/App/tiles/orders/FixingOrder/FixingOrderExecutedTile/ActionButtons.tsx';
import BackButton from '@/App/tiles/orders/FixingOrder/FixingOrderExecutedTile/BackButton.tsx';
import { useAccount } from '@/App/tiles/orders/hooks.tsx';
import { getOrderWay, getStatusClass, isOrderEditable, mapStatusToColor } from '@/App/tiles/orders/utils.ts';
import { checkIfUserCanTrade, useUserContext } from '@/context/UserContext.tsx';
import { finalStatuses, type FixingPriceType } from '@/store/bulkOrder/state.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectOrderEntryByIdFromBlotter, selectTradeIdFromFixingTile } from '@/store/orders/fixing/selectors.ts';
import { removeTile } from '@/store/tiles/slice.ts';

const FixingOrderExecutedTile = () => {
  const tileId = useFixingTileId();
  const dispatch = useAppDispatch();
  const { formatMessage, formatNumber } = useIntl();

  const userContextValue = useUserContext();
  const readOnly = !checkIfUserCanTrade(userContextValue);

  const tradeId = useAppSelector((state) => selectTradeIdFromFixingTile(state, tileId));

  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const account = useAccount(orderFromBlotter);

  const isFinal = finalStatuses.includes(orderFromBlotter?.status ?? '');
  const color = mapStatusToColor(orderFromBlotter?.status);

  const canEditOrder = isOrderEditable(orderFromBlotter?.status, tradeId);

  function handleClose() {
    dispatch(removeTile(tileId));
  }

  if (isNotDefined(orderFromBlotter)) {
    console.error(`[FixingOrderExecutedTile] Tile with id ${tileId} not found in blotter.`);
    return <ExecutionPendingOrderStatus />;
  }

  const way = getOrderWay(
    orderFromBlotter.currencyPair,
    orderFromBlotter?.notionalCurrency,
    orderFromBlotter?.yourWay ?? 'Buy',
  );

  return (
    <div className="d-flex w-100 h-100">
      <VStack className="bg-lvl1 py-2" fullWidth fullHeight>
        <HStack className="m-2">
          <div>
            <span data-e2e="order-tradeId" className=" text-primary fw-bold me-4px">
              Order {tradeId}
            </span>
            <span data-e2e="order-status-text" className={getStatusClass(orderFromBlotter?.status)}>
              {orderFromBlotter?.status}
            </span>
          </div>
          <button type="button" className="btn btn-flat-primary" onClick={handleClose} data-e2e="close-tile">
            <em className="icon icon-sm">close</em>
          </button>
        </HStack>
        <div className={`bg-${color} bg-opacity-10 text-${color} p-3 my-3`}>
          <ProgressBar
            executedAmount={orderFromBlotter?.executedAmount}
            notional={orderFromBlotter?.notional}
            color={color}
            isFinal={isFinal}
          />

          <div className="my-3">
            <span className="summary-text">
              <Summary
                account={account}
                orderType={orderFromBlotter?.orderType ?? 'Call'}
                way={orderFromBlotter?.yourWay ?? null}
                isGtc={orderFromBlotter?.isGtc ?? false}
                notional={orderFromBlotter?.notional ?? null}
                currency={orderFromBlotter?.notionalCurrency ?? null}
                limitPrice={orderFromBlotter?.limitPrice ?? null}
                expiryDay={orderFromBlotter?.expiryDay ?? null}
                expiryTime={orderFromBlotter?.expiryTime ?? null}
                currencyPair={orderFromBlotter?.currencyPair ?? null}
                fixingPlaceOrTime={orderFromBlotter?.fixingTime ?? orderFromBlotter?.fixingPlace ?? null}
                fixingDate={orderFromBlotter?.fixingDateUtc ?? null}
                fixingRef={orderFromBlotter?.fixingBenchmark ?? null}
                priceType={(orderFromBlotter?.fixingPriceType as FixingPriceType) ?? null} // modify type when fixing is added
                finalStatus={isFinal}
              />
            </span>
          </div>
        </div>

        <div>
          <ExecutedTileField label="currencyPair" value={orderFromBlotter?.currencyPair} />
          <ExecutedTileField label="orderType" value={orderFromBlotter?.orderType} />
          <ExecutedTileField
            label="way"
            value={`${formatMessage({ id: way === 'Buy' ? 'way.buy' : 'way.sell' })} ${orderFromBlotter.notionalCurrency}`}
          />
          <ExecutedTileField
            label="notional"
            value={`${isDefined(orderFromBlotter?.notional) ? formatNumber(Number(orderFromBlotter?.notional)) : ''} ${orderFromBlotter?.notionalCurrency}`}
          />
          <ExecutedTileField label="fixingReference" value={orderFromBlotter?.fixingBenchmark} />
          <ExecutedTileField
            label="fixingPriceType"
            value={formatMessage({
              id:
                orderFromBlotter?.fixingPriceType === 'BIDASK'
                  ? 'order.form.priceType.BIDASK'
                  : 'order.form.priceType.MID',
            })}
          />
          <ExecutedTileField label="fixingDate" value={orderFromBlotter?.fixingDateUtc} />
          {isDefined(orderFromBlotter.expiryDay) && (
            <ExecutedTileField label="fixingDeliveryDate" value={orderFromBlotter?.expiryDay} />
          )}
          {isDefined(orderFromBlotter?.fixingTime) && (
            <ExecutedTileField label="fixingTime" value={orderFromBlotter?.fixingTime} />
          )}
          {isDefined(orderFromBlotter?.fixingPlace) && (
            <ExecutedTileField label="fixingPlace" value={orderFromBlotter?.fixingPlace} />
          )}
          <ExecutedTileField label="fixingMarginType" value={orderFromBlotter?.fixingMarginType} />
          {isDefined(orderFromBlotter?.marginInBps) && (
            <ExecutedTileField label="fixingCommercialMargin" value={orderFromBlotter?.marginInBps} />
          )}
        </div>
        <Spacer />
        {canEditOrder ? (
          <ActionButtons tradeId={tradeId} currencyPair={orderFromBlotter?.currencyPair} />
        ) : (
          <HStack
            horizontalAlignment="end"
            verticalAlignment="center"
            margin="8px"
            gap="8px"
            data-e2e="action-buttons-container"
          >
            <BackButton currencyPair={orderFromBlotter?.currencyPair} readOnly={readOnly} />
          </HStack>
        )}
      </VStack>
    </div>
  );
};

export default FixingOrderExecutedTile;
